import type { UserUuid } from "../identity";
// src/types/healthcare/ClinikoFormImportIssueTypes.ts

/**
 * The Cliniko form-import issue ledger (S11, ClickUp 14ygtkj65bx).
 *
 * ⚠️ **There is no answer value here, and none may be added.** The ledger records that an answer
 * could not be placed and enough to repair the template — ids, counts, the reason token and the
 * clinic-authored section/question NAMES. What the patient wrote is not stored and is not served.
 */

/** `ClinikoFormImportIssue::ISSUE_*`. */
export type ClinikoFormImportIssueType = "unmatched" | "ambiguous" | "type_mismatch" | "unmatchable";

/** The list filter. `unresolved` is the default — the only set an operator works from. */
export type ClinikoFormImportIssueStatus = "unresolved" | "resolved" | "all";

/** A row of `cliniko_form_import_issues`, as the controller's `format()` serialises it. */
export interface ClinikoFormImportIssue {
	id: number;
	/** The Cliniko patient form the issue was found on — present for every issue type. */
	cliniko_patient_form_id: number;
	cliniko_template_id: number | null;
	/** The local rows, when they exist. `unmatchable` has neither — the form was not written. */
	assigned_form_id: number | null;
	form_id: number | null;
	issue_type: ClinikoFormImportIssueType;
	/** The importer's own token, verbatim (`duplicate_field_name`, `no_such_section`, …). */
	reason: string;
	section_name: string;
	question_name: string;
	/** `unmatchable` only: how much of the form was placeable. */
	answered: number | null;
	matched: number | null;
	first_seen_at: string | null;
	last_seen_at: string | null;
	resolved_at: string | null;
	/** An auth-service user id — this service has no user table to expand it against. */
	resolved_by: UserUuid | null;
}

export interface ClinikoFormImportIssueListResponse {
	data: ClinikoFormImportIssue[];
	total: number;
	page: number;
	per_page: number;
}

export interface ClinikoFormImportIssueResolveResponse {
	data: ClinikoFormImportIssue;
	/** True when the row was already closed — a double-click, not an error. */
	already_resolved: boolean;
}

/**
 * What each kind of issue is, and what an operator actually does about it.
 *
 * Every one of them is answered by editing the form TEMPLATE and re-running the importer with
 * `--since` over the affected window — the remedies differ, the shape of the fix does not.
 */
export const CLINIKO_ISSUE_TYPES: {
	key: ClinikoFormImportIssueType;
	label: string;
	color: string;
	guidance: string;
}[] = [
	{
		key: "unmatched",
		label: "Unmatched",
		color: "orange",
		guidance:
			"The section or question named below is not in the template as it stands today — reworded, removed, or never named. Rename it back to the name shown, then re-run the import for that window.",
	},
	{
		key: "ambiguous",
		label: "Ambiguous",
		color: "gold",
		guidance:
			"Two fields share a normalised name, or two Cliniko questions claim one field. Neither answer was taken, because taking one would be a coin flip. Give the fields distinct names, then re-run the import.",
	},
	{
		key: "type_mismatch",
		label: "Type mismatch",
		color: "volcano",
		guidance:
			"The answer's shape cannot be that field's type — a free-text reply into a date, say. Change the field's type to match what patients actually answered, then re-run the import.",
	},
	{
		key: "unmatchable",
		label: "Form not imported",
		color: "red",
		guidance:
			"Under half of this completed form's answers could be placed, so none of it was imported. Compare the answered/matched counts: a near miss is a handful of renamed questions, a wide gap means the template has moved on entirely.",
	},
];

export const CLINIKO_ISSUE_TYPE_LABELS: Record<ClinikoFormImportIssueType, string> = CLINIKO_ISSUE_TYPES.reduce(
	(labels, type) => ({ ...labels, [type.key]: type.label }),
	{} as Record<ClinikoFormImportIssueType, string>
);
